import { useState, useEffect } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, /* FolderOpen, Eye, */ Upload, Share2,
  Search, FileSearch, PanelLeftClose, ChevronRight, LogOut, UserCog, ScrollText, Receipt,
  LayoutGrid, Trash2, Building2, CalendarDays,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { ROLE_LABELS } from "@/lib/rbac";
import { useRbac } from "@/hooks/useRbac";
import XLSyncLogo from "@/components/brand/XLSyncLogo";
import UploadTray from "@/components/UploadTray";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const ALL_NAV_ITEMS = [
  { path: "/", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/weekly-dashboard", icon: CalendarDays, label: "Weekly Dashboard" },
  { path: "/patients", icon: Users, label: "Patients" },
  // { path: "/studies", icon: FolderOpen, label: "Studies" },
  // { path: "/upload", icon: Upload, label: "Upload" },
  // { path: "/viewer", icon: Eye, label: "Viewer" },
  { path: "/reports", icon: FileSearch, label: "Reports" },
  { path: "/sharing", icon: Share2, label: "Sharing" },
  { path: "/search", icon: Search, label: "Search" },
];

function initials(name) {
  if (!name) return "?";
  return name
    .split(" ")
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

const BrandMark = ({ size = "md", collapsed = false }) => (
  <XLSyncLogo
    size={size === "sm" ? "sm" : "md"}
    variant={collapsed ? "icon" : "full"}
    className={collapsed ? "" : "w-full origin-left scale-[1.12]"}
    data-testid="sidebar-logo"
  />
);

export default function Sidebar({ className, mobile = false, onNavigate }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const { user, canUpload, canAccessRoute, canManageUsers, canViewAudit, canViewBilling, canApproveStudyDeletion } = useRbac();
  const isViewerPage = location.pathname.startsWith("/viewer");
  const [collapsed, setCollapsed] = useState(isViewerPage);

  useEffect(() => {
    setCollapsed(isViewerPage);
  }, [isViewerPage]);

  const isCollapsed = mobile ? false : collapsed;
  const handleToggle = () => setCollapsed((v) => !v);

  const visibleNavItems = ALL_NAV_ITEMS.filter((item) => canAccessRoute(item.path));

  const handleNavClick = () => {
    onNavigate?.();
  };

  const handleUploadClick = () => {
    const from = `${location.pathname}${location.search}`;
    navigate("/upload", {
      state: from && from !== "/upload" ? { from } : undefined,
    });
    onNavigate?.();
  };

  const handleSignOut = async () => {
    await signOut();
    toast.success("Signed out");
    navigate("/login", { replace: true });
    onNavigate?.();
  };

  return (
    <aside
      data-testid="sidebar"
      className={cn(
        "h-full flex flex-col flex-shrink-0 border-r border-[var(--xl-sidebar-line)] bg-[var(--xl-sidebar)] text-[var(--xl-sidebar-text)] transition-[width] duration-200 ease-out",
        !mobile && "h-screen",
        isCollapsed ? "w-[72px] px-2 py-2.5" : mobile ? "w-full px-3 py-3" : "w-[240px] px-3 py-2.5",
        className
      )}
    >
      {/* Brand + collapse toggle */}
      <div
        className={cn(
          "relative flex items-center mb-2 min-h-0 w-full",
          isCollapsed ? "justify-center" : "justify-start pr-8",
        )}
      >
        <BrandMark size={isCollapsed ? "sm" : "md"} collapsed={isCollapsed} />
        {!isCollapsed && !mobile && (
          <button
            type="button"
            onClick={handleToggle}
            title="Collapse sidebar"
            className="absolute right-0 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full grid place-items-center text-[var(--xl-sidebar-mut)] hover:bg-[var(--xl-sidebar-hover)] transition-colors"
            aria-label="Collapse sidebar"
          >
            <PanelLeftClose className="w-[17px] h-[17px]" />
          </button>
        )}
      </div>

      {/* Upload CTA — only for roles with upload permission */}
      {canUpload && (
        <button
          type="button"
          onClick={handleUploadClick}
          title="Upload New Patient"
          className={cn(
            "flex items-center justify-center gap-2 mb-4 rounded-full bg-[var(--xl-black)] text-white font-semibold text-[13.5px] cursor-pointer hover:bg-black/85 transition-colors",
            isCollapsed ? "py-2.5 px-0 w-full" : "py-2.5 px-3 w-full"
          )}
          data-testid="sidebar-upload"
        >
          <Upload className="w-[17px] h-[17px] flex-shrink-0" />
          {!isCollapsed && <span>Upload New Patient</span>}
        </button>
      )}

      {isCollapsed && !mobile && (
        <button
          type="button"
          onClick={handleToggle}
          title="Expand sidebar"
          className="grid place-items-center py-1.5 pb-3 text-[var(--xl-sidebar-mut)] hover:text-[var(--xl-sidebar-text)] transition-colors"
          aria-label="Expand sidebar"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      )}

      {/* Navigation */}
      <nav className="flex-1 flex flex-col gap-0.5 overflow-y-auto min-h-0">
        {visibleNavItems.map((item) => {
          const isActive =
            item.path === "/"
              ? location.pathname === "/"
              : location.pathname.startsWith(item.path);
          const Icon = item.icon;
          return (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={handleNavClick}
              title={isCollapsed ? item.label : undefined}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              className={cn(
                "flex items-center rounded-full text-sm transition-colors duration-150",
                isCollapsed ? "justify-center py-2.5 px-0" : "gap-3 py-2.5 px-3",
                isActive
                  ? "sidebar-item-active"
                  : "font-medium text-[var(--xl-sidebar-mut)] hover:bg-[var(--xl-sidebar-hover)] hover:text-[var(--xl-sidebar-text)]"
              )}
            >
              <Icon className={cn("w-[19px] h-[19px] flex-shrink-0", isActive && "text-[var(--xl-primary)]")} />
              {!isCollapsed && <span className="truncate">{item.label}</span>}
            </NavLink>
          );
        })}
      </nav>

      {!mobile && <UploadTray embedded collapsed={isCollapsed} />}

      {/* User footer */}
      <div className="mt-auto pt-3.5 border-t border-[var(--xl-sidebar-line)]">
        <DropdownMenu>
          <DropdownMenuTrigger
            data-testid="user-menu-trigger"
            className={cn(
              "w-full rounded-full outline-none transition-colors focus-visible:ring-2 focus-visible:ring-primary/30",
              "flex items-center gap-2.5 min-w-0 hover:bg-[var(--xl-sidebar-hover)]",
              isCollapsed ? "justify-center p-1.5" : "px-2 py-1.5",
            )}
            aria-label="Open user menu"
          >
            <div
              className="w-[34px] h-[34px] rounded-full flex-shrink-0 grid place-items-center text-xs font-bold text-white bg-[var(--xl-black)]"
              aria-hidden
            >
              {initials(user?.name)}
            </div>
            {!isCollapsed && (
              <div className="min-w-0 leading-tight text-left">
                <div className="font-semibold text-[13px] truncate">{user?.name ?? "—"}</div>
                <div className="text-[11.5px] text-[var(--xl-sidebar-mut)] capitalize truncate">
                  {ROLE_LABELS[user?.role] || user?.role?.replace(/_/g, " ") || ""}
                </div>
              </div>
            )}
          </DropdownMenuTrigger>

          <DropdownMenuContent align={isCollapsed ? "center" : "end"} className="w-56">
            <DropdownMenuLabel className="text-xs">
              <div data-testid="header-user-name">{user?.name || "Signed in"}</div>
              <div className="font-normal text-muted-foreground" data-testid="header-user-email">
                {user?.email}
              </div>
              <div className="font-normal text-[var(--xl-primary)] mt-1" data-testid="header-user-role">
                {ROLE_LABELS[user?.role] || user?.role?.replace(/_/g, " ")}
              </div>
            </DropdownMenuLabel>

            {(canAccessRoute("/organizations") || canViewBilling || canManageUsers || canViewAudit || canApproveStudyDeletion) && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground py-1">
                  Settings
                </DropdownMenuLabel>
              </>
            )}

            {canAccessRoute("/organizations") && (
              <DropdownMenuItem
                data-testid="menu-organizations"
                className="cursor-pointer"
                onClick={() => {
                  navigate("/organizations");
                  onNavigate?.();
                }}
              >
                <Building2 className="w-4 h-4 mr-2" /> Companies
              </DropdownMenuItem>
            )}

            {canViewBilling && (
              <DropdownMenuItem
                data-testid="menu-billing"
                className="cursor-pointer"
                onClick={() => {
                  navigate("/billing");
                  onNavigate?.();
                }}
              >
                <Receipt className="w-4 h-4 mr-2" /> Billing
              </DropdownMenuItem>
            )}

            {canApproveStudyDeletion && (
              <DropdownMenuItem
                data-testid="menu-pending-deletions"
                className="cursor-pointer"
                onClick={() => {
                  navigate("/studies/pending-deletions");
                  onNavigate?.();
                }}
              >
                <Trash2 className="w-4 h-4 mr-2" /> Pending Deletions
              </DropdownMenuItem>
            )}

            {canManageUsers && (
              <DropdownMenuItem
                data-testid="menu-users"
                className="cursor-pointer"
                onClick={() => {
                  navigate("/users");
                  onNavigate?.();
                }}
              >
                <UserCog className="w-4 h-4 mr-2" /> User Management
              </DropdownMenuItem>
            )}

            {canAccessRoute("/dashboard-cards") && (
              <DropdownMenuItem
                data-testid="menu-dashboard-cards"
                className="cursor-pointer"
                onClick={() => {
                  navigate("/dashboard-cards");
                  onNavigate?.();
                }}
              >
                <LayoutGrid className="w-4 h-4 mr-2" /> Dashboard Cards
              </DropdownMenuItem>
            )}

            {canViewAudit && (
              <DropdownMenuItem
                data-testid="menu-audit"
                className="cursor-pointer"
                onClick={() => {
                  navigate("/audit");
                  onNavigate?.();
                }}
              >
                <ScrollText className="w-4 h-4 mr-2" /> Audit Trail
              </DropdownMenuItem>
            )}

            <DropdownMenuSeparator />
            <DropdownMenuItem
              data-testid="menu-logout"
              onClick={handleSignOut}
              className="text-destructive cursor-pointer"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </aside>
  );
}
